import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Drawer, Divider, Button, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import HomeIcon from "@mui/icons-material/Home";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LinkIcon from "@mui/icons-material/Link";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import BuildIcon from "@mui/icons-material/Build";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4 from "@mui/icons-material/Brightness4";
import Brightness7 from "@mui/icons-material/Brightness7";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import "../../styles/Sidebar.css";

const ThemeToggle = ({ collapsed, theme, onToggleTheme }) => {
  return collapsed ? (
    <IconButton
      className="theme-toggle-btn"
      onClick={onToggleTheme}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      sx={{
        margin: "0 auto 15px",
        color: "var(--text-color)",
        background: "var(--gradient-primary)",
        borderRadius: "var(--border-radius-sm)",
        "&:hover": {
          background: "var(--gradient-primary)",
          transform: "scale(1.1)",
          boxShadow: "var(--box-shadow-hover)",
        },
      }}
    >
      {theme === "dark" ? <Brightness7 /> : <Brightness4 />}
    </IconButton>
  ) : (
    <Button
      className="theme-toggle-btn"
      onClick={onToggleTheme}
      startIcon={theme === "dark" ? <Brightness7 /> : <Brightness4 />}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      sx={{
        margin: "0 15px 15px",
        textTransform: "none",
        color: "var(--text-color)",
        fontFamily: "var(--font-family-base)",
        fontWeight: 600,
      }}
    >
      {theme === "dark" ? "Light Mode" : "Dark Mode"}
    </Button>
  );
};

const Sidebar = ({ collapsed, setCollapsed, theme, onToggleTheme }) => {
  const [mobileActive, setMobileActive] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const pages = [
    { name: "Home", path: "/", icon: <HomeIcon /> },
    { name: "Customer Profiling", path: "/customer-profiling", icon: <ManageAccountsIcon /> },
    { name: "Initiate Trade", path: "/view-and-upload", icon: <UploadFileIcon /> },
    { name: "Transaction Chain", path: "/transaction-chain", icon: <LinkIcon /> },
    { name: "Monitoring", path: "/monitoring", icon: <MonitorHeartIcon /> },
    { name: "Reporting Page", path: "/reporting", icon: <AssessmentIcon /> },
    { name: "Configure Bot", path: "/configure-bot", icon: <SettingsIcon /> },
    { name: "Integrated Tools", path: "/integrated-tools", icon: <BuildIcon /> },
    { name: "Payment Method Setup", path: "/payment-method-setup", icon: <SettingsApplicationsIcon /> },
    { name: "Admin Panel", path: "/admin-panel", icon: <AdminPanelSettingsIcon /> },
  ];

  const isAuthenticated = !!localStorage.getItem("authToken");

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  const toggleMobileSidebar = () => {
    setMobileActive((prev) => !prev);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileActive) {
      setMobileActive(false);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <IconButton
        className="mobile-menu-button"
        onClick={toggleMobileSidebar}
        sx={{
          display: { xs: "block", sm: "none" },
          position: "fixed",
          top: "var(--spacing-sm)",
          left: "var(--spacing-sm)",
          zIndex: 20000,
          background: "var(--gradient-primary)",
          color: "var(--text-color)",
          borderRadius: "var(--border-radius-sm)",
          "&:hover": {
            background: "var(--gradient-primary)",
            transform: "scale(1.1)",
            boxShadow: "var(--box-shadow-hover)",
          },
        }}
        aria-label="Toggle mobile sidebar"
      >
        <MenuIcon />
      </IconButton>
      <div
        className={`sidebar-overlay ${mobileActive ? "active" : ""}`}
        onClick={toggleMobileSidebar}
      />
      <Drawer
        variant="permanent"
        anchor="left"
        className={`sidebar ${collapsed ? "collapsed" : ""} ${
          mobileActive ? "mobile-active" : ""
        }`}
        PaperProps={{
          className: `sidebar ${collapsed ? "collapsed" : ""} ${
            mobileActive ? "mobile-active" : ""
          }`,
          sx: {
            background: "var(--card-background)",
            backdropFilter: "var(--backdrop-blur)",
            color: "var(--text-color)",
            borderRight: "1px solid var(--border-color)",
            boxShadow: "var(--box-shadow)",
            transition: "var(--transition-cubic)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <div className="sidebar-header">
          <div className="logo">
            <img 
              src={theme === "dark" ? "/images/white_logo.png" : "/images/logo.png"} 
              alt="Logo" 
              className="logo-img" 
            />
          </div>
        </div>

        <Divider className="sidebar-divider" />

        <ul className="sidebar-list">
          <li title={collapsed ? "Toggle Sidebar" : ""}>
            <button
              className="sidebar-toggle-btn"
              onClick={toggleSidebar}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <span className="sidebar-icon">
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </span>
              {!collapsed && <span className="sidebar-label">Toggle Sidebar</span>}
            </button>
          </li>
          {pages.map((page) => (
            <li key={page.name} title={collapsed ? page.name : ""}>
              <button
                onClick={() => handleNavigation(page.path)}
                className={isActive(page.path) ? "active" : ""}
              >
                <span className="sidebar-icon">{page.icon}</span>
                {!collapsed && <span className="sidebar-label">{page.name}</span>}
              </button>
            </li>
          ))}
        </ul>

        <div style={{ marginTop: "auto" }}>
          <ThemeToggle collapsed={collapsed} theme={theme} onToggleTheme={onToggleTheme} />
          
          {isAuthenticated && (
            <>
              {!collapsed && (
                <Button
                  className="logout-button"
                  onClick={handleLogout}
                  sx={{
                    display: "block",
                    margin: "0 15px 20px",
                    background: "transparent",
                    color: "var(--primary-color)",
                    border: "2px solid var(--primary-color)",
                    textTransform: "none",
                    fontFamily: "var(--font-family-base)",
                    fontWeight: 600,
                    "&:hover": {
                      background: "var(--gradient-primary)",
                      transform: "translateY(-2px)",
                      boxShadow: "var(--box-shadow-hover)",
                    },
                  }}
                  aria-label="Log out"
                >
                  Logout
                </Button>
              )}
              
              {collapsed && (
                <IconButton
                  className="logout-icon-button"
                  onClick={handleLogout}
                  sx={{
                    margin: "0 auto 20px",
                    display: "block",
                    color: "var(--text-color)",
                    background: "transparent",
                    border: "2px solid var(--primary-color)",
                    "&:hover": {
                      background: "var(--gradient-primary)",
                      transform: "scale(1.1)",
                      boxShadow: "var(--box-shadow-hover)",
                    },
                  }}
                  aria-label="Log out"
                >
                  <LogoutIcon />
                </IconButton>
              )}
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default Sidebar;